import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { LayoutGrid, PenTool, Plus, Trash2 } from 'lucide-react';
import { useCanvasList, useDeleteCanvas } from '../features/canvas/hooks/useCanvas';
import { StaggerList, ListItem } from '../components/ui/Motion.js';

const TOP_COLORS = [
  'border-t-xd-info',
  'border-t-xd-action',
  'border-t-xd-action-end',
  'border-t-xd-badge',
];

export default function CanvasList({
  embedded = false,
  onNew,
}: {
  /** When true, renders as a tab panel inside Visuales (no own header). */
  embedded?: boolean;
  onNew?: () => void;
}) {
  const navigate = useNavigate();
  const { data: canvases, isLoading } = useCanvasList();
  const deleteCanvas = useDeleteCanvas();

  const handleNew = () => (onNew ? onNew() : navigate('/canvas/new'));

  const handleDelete = (e: React.MouseEvent, canvasId: string) => {
    e.stopPropagation();
    deleteCanvas.mutate(canvasId);
  };

  return (
    <div className={embedded ? '' : 'page-enter max-w-6xl mx-auto px-6 py-8'}>
      {/* Header */}
      {!embedded && (
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-text">Canvases</h1>
            <p className="text-sm text-text-muted mt-1">
              {canvases ? `${canvases.length} pizarrones` : 'Tus pizarrones de ideas'}
            </p>
          </div>
          <button
            onClick={handleNew}
            className="inline-flex items-center gap-2 rounded-lg bg-xd-action px-4 py-2 text-sm font-semibold text-[var(--xds-fg)] hover:bg-xd-action-hover transition-colors"
          >
            <Plus className="h-4 w-4" />
            Nuevo canvas
          </button>
        </div>
      )}

      {/* Skeleton loading */}
      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="bg-surface border border-border rounded-xl overflow-hidden">
              <div className="aspect-video bg-surface-2 animate-pulse" />
              <div className="p-4 space-y-2">
                <div className="h-4 bg-surface-2 rounded animate-pulse w-3/4" />
                <div className="h-3 bg-surface-2 rounded animate-pulse w-1/2" />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && (!canvases || canvases.length === 0) && (
        <div className="flex flex-col items-center py-24 border-2 border-dashed border-border rounded-2xl text-center">
          <LayoutGrid className="h-10 w-10 text-text-subtle" />
          <p className="text-text-muted text-base mt-4">Tu pizarron de ideas esta vacio</p>
          <button
            onClick={handleNew}
            className="inline-flex items-center gap-2 rounded-lg bg-xd-action px-4 py-2 text-sm font-semibold text-[var(--xds-fg)] hover:bg-xd-action-hover transition-colors mt-5"
          >
            Crear canvas
          </button>
        </div>
      )}

      {/* Grid */}
      {canvases && canvases.length > 0 && (
        <StaggerList className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {canvases.map((canvas, index) => (
            <ListItem key={canvas.id}>
              <button
                onClick={() => navigate(`/canvas/${canvas.id}`)}
                className={`card-hover group block w-full h-full text-left bg-surface border border-border rounded-xl overflow-hidden border-t-2 ${TOP_COLORS[index % TOP_COLORS.length]}`}
              >
                {/* Thumbnail: aspect-video gradient with PenTool icon */}
                <div className="aspect-video bg-gradient-to-br from-xd-info/10 to-xd-badge/10 flex items-center justify-center group-hover:from-xd-info/20 group-hover:to-xd-badge/20 transition-colors">
                  <PenTool className="h-8 w-8 text-xd-info/50 group-hover:text-xd-info transition-colors" />
                </div>

                {/* Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-text truncate">{canvas.title}</h3>
                    <button
                      onClick={(e) => handleDelete(e, canvas.id)}
                      className="inline-flex items-center gap-2 rounded-lg text-sm font-medium text-xd-error hover:bg-xd-error/10 px-1.5 py-1 transition-colors opacity-0 group-hover:opacity-100"
                      aria-label="Eliminar canvas"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-xs text-text-subtle mt-1">
                    Actualizado{' '}
                    {formatDistanceToNow(new Date(canvas.updatedAt), {
                      addSuffix: true,
                      locale: es,
                    })}
                  </p>

                  {/* Tags */}
                  {canvas.tags.length > 0 && (
                    <div className="flex gap-1 flex-wrap mt-2">
                      {canvas.tags.slice(0, 3).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 bg-surface-2 text-text-muted text-xs rounded-full"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </button>
            </ListItem>
          ))}
        </StaggerList>
      )}
    </div>
  );
}
